import type { ClaimedPlayer, PlayerAlliance, PlayerTribe } from '@/types/player'
import type { Group } from '@/types/user'

/** What the sidebar card shows: one character and the world it plays on. */
export interface SidebarPlayer {
  player: {
    id: number
    alias: string
    tribe: PlayerTribe | null
    alliance: PlayerAlliance | null
    rank: number | null
    population: number | null
    villages: number | null
  }
  server: { id: number; name: string }
}

interface ServerRef {
  id: number
  server_name: string
}

/**
 * Picks the character for the sidebar card, from the two pieces of context the
 * header carries — the selected server and the selected group — which usually
 * point at the same world but don't have to.
 *
 * 1. The group's character, when the group plays on the selected server (or no
 *    server is selected): that membership is the one every group page uses, and
 *    it can differ from the character claimed on the server.
 * 2. Otherwise the character claimed on the selected server.
 * 3. Otherwise the group's character, on the group's world.
 *
 * The group payload only carries a bare player; its tribe and alliance come
 * from the claimed one when it is the same character.
 */
export function sidebarPlayer(
  selectedServer: ServerRef | null,
  selectedGroup: Group | null,
  playerOnServer: (serverId: number) => ClaimedPlayer | null,
): SidebarPlayer | null {
  const groupServer = selectedGroup?.server ?? null
  const groupMember = selectedGroup?.role.player ?? null

  const fromGroup = (): SidebarPlayer | null => {
    if (!groupServer || !groupMember) return null
    const claimed = playerOnServer(groupServer.id)
    const same = claimed?.id === groupMember.id ? claimed : null
    const bare = groupMember as Partial<ClaimedPlayer>
    return {
      player: {
        id: groupMember.id,
        alias: groupMember.alias,
        tribe: same?.tribe ?? null,
        alliance: same?.alliance ?? null,
        rank: same?.rank ?? bare.rank ?? null,
        population: same?.population ?? bare.population ?? null,
        villages: same?.num_villages ?? bare.num_villages ?? null,
      },
      server: { id: groupServer.id, name: groupServer.server_name },
    }
  }

  const fromServer = (server: ServerRef): SidebarPlayer | null => {
    const claimed = playerOnServer(server.id)
    if (!claimed) return null
    return {
      player: {
        id: claimed.id,
        alias: claimed.alias,
        tribe: claimed.tribe ?? null,
        alliance: claimed.alliance ?? null,
        rank: claimed.rank,
        population: claimed.population,
        villages: claimed.num_villages,
      },
      server: { id: server.id, name: server.server_name },
    }
  }

  if (groupServer && (!selectedServer || selectedServer.id === groupServer.id)) {
    return fromGroup() ?? fromServer({ id: groupServer.id, server_name: groupServer.server_name })
  }
  if (selectedServer) {
    const onServer = fromServer(selectedServer)
    if (onServer) return onServer
  }
  return fromGroup()
}
